<template>
	<section class="map-view">
		<CleanerModal
			:cleanerProp="cleaner"
			:languages="languages"
			:workingAreas="workingAreas"
			:shifts="shifts"
			:rooms="rooms"
			:supervisors="supervisors"
			:appPages="appPages"
			v-if="isopenCleanerModal"
			@closeModal="closeCleanerModal"
			@save="saveCleaner"
		/>
		<UserModal
			:userProp="user"
			:languages="languages"
			:workingAreas="workingAreas"
			v-if="isOpenUserModal"
			@closeModal="closeUserModal"
			@save="saveUser"
		/>
		<div class="custom-button-container">
			<TableSearch :tableTitles="tableTitles" @input="handleInput" />
			<template v-if="(isAdmin || isSupervisor) && routeName === 'cleaners'">
				<Button @click="openCleanerModal" skin="primary">{{ $t('new_cleaner') }}</Button>
			</template>
			<template v-if="isAdmin && routeName === 'users'">
				<Button @click="openUserModal" skin="primary">{{ $t('new_user') }}</Button>
			</template>
		</div>
		<Tabs :isRTL="isRTL" syncRoute>
			<Tab :label="$t('users')" :to="'/users'" exact>
				<section v-if="routeName === 'users'" class="map-details">
					<Loading v-if="isLoading" class="loader" />
					<template v-else>
						<Table
							v-if="filteredUsers.length"
							:tableData="filteredUsers"
							idField="id"
							:editOptions="{ remove: isAdmin, edit: isAdmin }"
							@editRow="editUser"
							@removeRow="removeUser"
							:isPaginated="true"
							:itemsPerPage="10"
						></Table>
						<div v-else>
							<h3>{{ $t('no_users_found') }}</h3>
						</div>
					</template>
				</section>
			</Tab>
			<Tab :label="$t('cleaners')" :to="'/users/cleaners'" exact>
				<section v-if="routeName === 'cleaners'" class="map-details">
					<Loading v-if="isLoading" class="loader" />
					<template v-else>
						<div
							v-if="showAssignmentCoverageBanner"
							class="assignment-coverage"
							role="button"
							tabindex="0"
							@click="showAssignmentCoverageDetails"
							@keydown.enter.prevent="showAssignmentCoverageDetails"
						>
							<span>{{ assignmentCoverageLabel }}</span>
							<span class="assignment-coverage-hint">{{ $t('tap_for_details') }}</span>
						</div>
						<Table
							v-if="filteredCleaners.length"
							idField="id"
							:tableData="filteredCleaners"
							:rowClass="cleanerRowClass"
							:editOptions="{ remove: isSupervisor || isAdmin, edit: isSupervisor || isAdmin }"
							@editRow="editCleaner"
							@removeRow="removeCleaner"
						></Table>
						<div v-else>
							<h3>{{ $t('no_cleaners_found') }}</h3>
						</div>
					</template>
				</section>
			</Tab>
		</Tabs>
	</section>
</template>

<script>
import Tabs from '@/modules/common/components/Tabs/Tabs'
import Tab from '@/modules/common/components/Tabs/Tab'
import Loading from '@/modules/common/components/Loading'
import Table from '@/modules/common/components/customTable/Table'
import Button from '@/modules/common/components/Button'
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import CleanerModal from '@/modules/users/components/CleanerModal'
import UserModal from '@/modules/users/components/UserModal'
import utilService from '@/modules/common/services/utilService.js'
import workingAreaService from '../../working-area/services/workingAreaService'
import shiftService from '../../control-panel/services/shiftService'
import roomService from '../../control-panel/services/roomService'
import swalService from '@/modules/common/services/swalService'
import { getUsers, getLanguages, getDefaultUser, saveUser, removeUser } from '../../control-panel/services/userService'
import {
	getFilterdContacts,
	removeContactById,
	saveContact,
	getDefaultContact,
	getAppPages,
	getAssignmentCheck,
} from '../../control-panel/services/contactService'
import { mapGetters } from 'vuex'

export default {
	name: 'Users',
	components: { Tabs, Tab, Loading, Table, Button, TableSearch, CleanerModal, UserModal },
	data() {
		return {
			users: [],
			cleaners: [],
			workingAreas: [],
			appPages: [],
			shifts: [],
			rooms: [],
			languages: [],
			tableTitles: [],
			isLoading: false,
			searchTxt: '',
			searchBy: '',
			isOpenUserModal: false,
			isopenCleanerModal: false,
			cleaner: getDefaultContact(),
			user: getDefaultUser(),
			assignmentCoverage: null,
			assignmentsDirty: false,
			pageDataRequestId: 0,
		}
	},
	async beforeRouteLeave(to, from, next) {
		const canLeave = await this.confirmLeaveAssignments()
		return next(canLeave)
	},
	methods: {
		async updatePageData() {
			const requestId = ++this.pageDataRequestId
			try {
				this.startLoading()
				this.users = []
				this.cleaners = []
				this.assignmentCoverage = null
				const { site: siteId } = this.filterSelected
				const [users, languages, workingAreas, shifts, rooms, appPages] = await Promise.all([
					getUsers({ siteId }),
					getLanguages(),
					workingAreaService.getWorkingAreas(this.filterSelected),
					shiftService.getShifts(siteId),
					roomService.getRooms({site: siteId}),
					getAppPages()
				])
				if (requestId !== this.pageDataRequestId) return
				this.users = users.filter((user) => (user.type !== 'admin' && user.level !== 'buzzz') || user.level === 'supervisor') || []
				this.languages = languages
				this.workingAreas = workingAreas
				this.shifts = shifts
				this.rooms = rooms
				this.appPages = appPages
				this.cleaners = await getFilterdContacts({
					siteId,
					userIds: this.users.map((user) => user.id) || []
				})
				if (requestId !== this.pageDataRequestId) return
				await this.refreshAssignmentCoverage(siteId)
				if (requestId !== this.pageDataRequestId) return
				this.setTableTitles()
			} catch (err) {
				if (requestId === this.pageDataRequestId) {
					this.errorMsg()
				}
			} finally {
				if (requestId === this.pageDataRequestId) {
					this.stopLoading()
				}
			}
		},
		async refreshAssignmentCoverage(siteId) {
			if (!siteId) {
				this.assignmentCoverage = null
				return
			}
			try {
				const result = await getAssignmentCheck(siteId)
				this.assignmentCoverage = {
					checkFailed: false,
					hasIssues: !!result?.hasIssues,
					unassignedCount: result?.summary?.unassignedCount || 0,
					overlapCount: result?.summary?.overlapCount || 0,
					unassignedRooms: result?.unassignedRooms || [],
					overlaps: result?.overlaps || [],
				}
			} catch (err) {
				this.assignmentCoverage = {
					checkFailed: true,
					hasIssues: false,
					unassignedCount: 0,
					overlapCount: 0,
					unassignedRooms: [],
					overlaps: [],
				}
			}
		},
		markAssignmentsDirty() {
			this.assignmentsDirty = true
		},
		clearAssignmentsDirty() {
			this.assignmentsDirty = false
		},
		async confirmLeaveAssignments() {
			if (!this.assignmentsDirty) return true

			const finished = await swalService.confirmYesNo({
				title: this.$t('have_you_finished_making_changes'),
				confirmButtonText: this.$t('yes'),
				cancelButtonText: this.$t('no'),
			})
			if (!finished.isConfirmed) {
				this.clearAssignmentsDirty()
				return true
			}

			const siteId = this.filterSelected?.site
			if (!siteId) {
				this.clearAssignmentsDirty()
				return true
			}

			let result
			try {
				result = await getAssignmentCheck(siteId)
			} catch (err) {
				this.errorMsg(this.$t('assignment_check_failed'))
				this.clearAssignmentsDirty()
				return true
			}
			if (!result?.hasIssues) {
				this.clearAssignmentsDirty()
				return true
			}

			const proceed = await swalService.showAssignmentIssues({
				title: this.$t('assignment_issues_found'),
				html: this.formatAssignmentIssuesHtml(result),
				confirmButtonText: this.$t('continue_anyway'),
				cancelButtonText: this.$t('stay_and_fix'),
			})
			if (proceed.isConfirmed) {
				this.clearAssignmentsDirty()
				return true
			}
			return false
		},
		formatAssignmentIssuesHtml(result) {
			const parts = []
			const unassigned = result.unassignedRooms || []
			const unassignedCount = result.summary?.unassignedCount ?? result.unassignedCount ?? unassigned.length
			if (unassignedCount > 0) {
				const names = unassigned.map((room) => room.name).join(', ')
				let block = `<strong>${this.$t('unassigned_rooms_title')}</strong><br/>${names}`
				if (unassignedCount > unassigned.length) {
					block += `<br/>${this.$t('and_n_more', { count: unassignedCount - unassigned.length })}`
				}
				parts.push(block)
			}
			const overlaps = result.overlaps || []
			const overlapCount = result.summary?.overlapCount ?? result.overlapCount ?? overlaps.length
			if (overlapCount > 0) {
				const lines = overlaps.map((overlap) => {
					const employees = (overlap.employees || []).map((e) => e.name).join(', ')
					return `${overlap.roomName} · ${overlap.shiftName}: ${employees}`
				}).join('<br/>')
				let block = `<strong>${this.$t('overlapping_assignments_title')}</strong><br/>${lines}`
				if (overlapCount > overlaps.length) {
					block += `<br/>${this.$t('and_n_more', { count: overlapCount - overlaps.length })}`
				}
				parts.push(block)
			}
			return parts.join('<br/><br/>')
		},
		showAssignmentCoverageDetails() {
			if (!this.showAssignmentCoverageBanner) return
			return swalService.showAssignmentIssues({
				title: this.$t('assignment_issues_found'),
				html: this.formatAssignmentIssuesHtml(this.assignmentCoverage),
				confirmButtonText: this.$t('OK'),
			})
		},
		async saveCleaner(cleaner) {
			try {
				await saveContact(cleaner)
				this.markAssignmentsDirty()
				swalService.savedMsg(this.$t('Your work has been saved'))
				this.closeCleanerModal()
				this.updatePageData()
			} catch (err) {
				this.errorMsg()
			}
		},
		async editCleaner({ id }) {
			try {
				const cleanerToEdit = this.findCleanerById(id)
				this.cleaner = cleanerToEdit
				this.openCleanerModal()
			} catch (err) {
				this.errorMsg()
			}
		},
		async removeCleaner(id) {
			try {
				const isRemoveConfirm = await swalService.confirmMsg()
				if (!isRemoveConfirm.value) return
				await removeContactById(id)
				this.markAssignmentsDirty()
				swalService.deletedMsg()
				this.updatePageData()
			} catch (err) {
				this.errorMsg()
			}
		},
		async saveUser(user) {
			try {
				user.sites = [this.filterSelected.site]
				await saveUser(user)
				swalService.savedMsg(this.$t('Your work has been saved'))
				this.closeUserModal()
				this.updatePageData()
			} catch (err) {
				this.errorMsg()
			}
		},
		async editUser({ id }) {
			try {
				if (!this.isAdmin) {
					this.errorMsg(`${this.$t('you_are_not_allowed_to_edit')}`)
					return
				}
				const userToEdit = this.findUserById(id)
				this.user = userToEdit
				this.openUserModal()
			} catch (err) {
				this.errorMsg()
			}
		},
		async removeUser(id) {
			try {
				if (!this.isAdmin) {
					this.errorMsg(`${this.$t('you_are_not_allowed_to_remove')}`)
					return
				}
				const isRemoveConfirm = await swalService.confirmMsg()
				if (!isRemoveConfirm.value) return
				await removeUser(id)
				swalService.deletedMsg()
				this.updatePageData()
			} catch (err) {
				this.errorMsg()
			}
		},
		findCleanerById(id) {
			return this.cleaners.find((cleaner) => cleaner.id === id)
		},
		findUserById(id) {
			return this.users.find((user) => user.id === id)
		},
		setTableTitles() {
			const excludedKeys = ['id', 'workingAreaNames']
			const cleanersToShow = this.cleanersToShow
			if (this.routeName === 'cleaners') {
				if (!cleanersToShow.length) return []
				this.tableTitles = Object.keys(cleanersToShow[0]).filter((key) => !excludedKeys.includes(key))
			} else if (this.routeName === 'users') {
				const usersToShow = this.usersToShow
				if (!usersToShow.length) return []
				this.tableTitles = Object.keys(usersToShow[0]).filter((key) => !excludedKeys.includes(key))
			}
		},

		handleInput(value, searchBy) {
			if (searchBy) this.searchBy = searchBy
			this.searchTxt = value
		},
		startLoading() {
			this.isLoading = true
		},
		stopLoading() {
			this.isLoading = false
		},
		openCleanerModal() {
			this.isopenCleanerModal = true
		},
		openUserModal() {
			this.isOpenUserModal = true
		},
		closeCleanerModal() {
			this.isopenCleanerModal = false
			this.cleaner = getDefaultContact()
		},
		closeUserModal() {
			this.isOpenUserModal = false
			this.user = getDefaultUser()
		},
		errorMsg(msg) {
			swalService.errorMsg(msg)
		},
		cleanerRowClass(cleaner) {
			return cleaner.works_at_site === '❌' ? 'row-muted' : ''
		},
	},
	computed: {
		...mapGetters(['isRTL', 'loggedUser', 'filterSelected']),
		filteredUsers() {
			return utilService.filterArrayOfObjectsWithString(this.usersToShow, this.searchTxt, this.searchBy)
		},
		filteredCleaners() {
			return utilService.filterArrayOfObjectsWithString(this.cleanersToShow, this.searchTxt, this.searchBy)
		},
		supervisors() {
			return this.usersToShow.filter((user) => user.level === 'supervisor')
		},
		cleanersToShow() {
			const yes = '✅'
			const no = '❌'
			return this.cleaners.map((cleaner) => {
				const {
					id,
					phone,
					supervisorFirstName,
					supervisorLastName,
					username,
					hasApp,
					notificationsActive,
					assignedToRooms,
					online,
					isWorkingInSite,
				} = cleaner
				return {
					id,
					phone,
					supervisor: `${supervisorFirstName} ${supervisorLastName}`,
					username,
					role: cleaner.role ? this.$t(cleaner.role) : '',
					works_at_site: isWorkingInSite ? yes : no,
					has_app: hasApp ? yes : no,
					notifications: notificationsActive ? yes : no,
					assigned_to_rooms: assignedToRooms ? yes : no,
					recently_active: online ? yes : no,
				}
			})
		},
		usersToShow() {
			return this.users.map((user) => {
				const { id, email, phone, firstName, lastName, username, type, level, jobFunction } = user
				return {
					id,
					email,
					phone,
					firstName,
					lastName,
					username,
					type,
					level,
					job_function: jobFunction ? this.$t(jobFunction) : ''
				}
			})
		},
		isAdmin() {
			return this.loggedUser.type === 'admin'
		},
		isSupervisor() {
			return this.loggedUser.level === 'supervisor'
		},
		routeName() {
			return this.$route.name
		},
		showAssignmentCoverageBanner() {
			return !!(
				this.assignmentCoverage &&
				!this.assignmentCoverage.checkFailed &&
				this.assignmentCoverage.hasIssues
			)
		},
		assignmentCoverageLabel() {
			if (!this.showAssignmentCoverageBanner) return ''
			return this.$t('assignment_coverage_issues', {
				unassigned: this.assignmentCoverage.unassignedCount,
				overlaps: this.assignmentCoverage.overlapCount,
			})
		}
	},
	watch: {
		'$store.getters.filterSelected.site': {
			handler() {
				this.updatePageData()
			},
			immediate: true
		},
		'$route.name': {
			handler() {
				this.setTableTitles()
			},
			immediate: true
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.map-view {
	position: relative;
	background-color: white;
	min-height: 86vh;
}
.assignment-coverage {
	margin: 0 0 16px;
	padding: 10px 14px;
	border-radius: 8px;
	background: #fef3c7;
	color: #92400e;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	&:hover {
		background: #fde68a;
	}
	.assignment-coverage-hint {
		font-weight: 500;
		opacity: 0.85;
		white-space: nowrap;
	}
}
.custom-button-container {
	height: 80px;
	display: flex;
	align-items: center;
	margin-inline-end: 20px;
	text-transform: uppercase;
	position: absolute;
	z-index: 3;
	right: 0;
	@include rtl {
		left: 0;
		right: unset;
	}
	span {
		@include modal-input-label;
		margin-bottom: 0px;
		font-size: 13px;
	}
	input {
		@include modal-input;
		margin-inline-start: 5px;
		margin-bottom: 0px;
		padding: 0 5px;
		width: 150px;
	}
	button {
		font-size: 12px;
		font-weight: bold;
	}
}
</style>
